import { RouterProvider, createRouter } from "@tanstack/react-router"
import { type ReactElement } from "react"
import { Provider as UrqlProvider, cacheExchange, createClient, fetchExchange } from "urql"

import { routeTree } from "../../routeTree.gen"

import { hasMockSelections, mockFetch } from "@/mocks/mockFetch"
import { useAuthStore } from "@/stores/authStore"
import { urqlAuthExchange } from "@/utils/auth"
import { getApiOrigin } from "@/utils/getApiOrigin"
import { networkErrorExchange } from "@/utils/urql/networkErrorExchange"

// Define router context type
export interface RouterContext {
  auth: ReturnType<typeof useAuthStore.getState>
  queryClient: ReturnType<typeof createUrqlClient>
}

// Create URQL client
const createUrqlClient = (disableCache = true) =>
  createClient({
    url: getApiOrigin(),
    // Dev-only mock transport, installed only when VITE_MOCK_OPERATIONS selects
    // something — otherwise the native fetch is used and mockFetch is inert.
    // See src/mocks/contracts/README.md.
    ...(import.meta.env.DEV && hasMockSelections ? { fetch: mockFetch } : {}),
    exchanges: disableCache
      ? [networkErrorExchange, urqlAuthExchange, fetchExchange]
      : [cacheExchange, networkErrorExchange, urqlAuthExchange, fetchExchange],
  })

// Create router with proper context type
export const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient: undefined!,
  } as RouterContext,
  defaultPreload: "intent",
  defaultPreloadDelay: 0,
})

// Register router type
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

export const queryClient = createUrqlClient()

useAuthStore.getState().initStore() // Initialize auth store and Token Coockies on module load

// GraphQL and Router Provider component
export function GraphqlRouterProvider(): ReactElement {
  const auth = useAuthStore()

  return (
    <UrqlProvider value={queryClient}>
      <RouterProvider
        router={router}
        context={{
          auth,
          queryClient,
        }}
      />
    </UrqlProvider>
  )
}
